import { useState, type ChangeEvent } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError } from '../api/client';
import { listBusinessPhotos, deleteBusinessPhoto, patchBusinessPhoto, uploadBusinessPhoto } from '../api/endpoints/photos';
import type { BusinessPhoto } from '../api/schemas';
import { buildPhotoUploadPlan, type PhotoUploadPlan } from '../lib/photoPipeline';
import { useMutationAttempt } from '../lib/use-mutation-attempt';
import { ErrorBanner } from './ErrorBanner';

type PhotoManagerProps = { businessId: string; photos: BusinessPhoto[] };
type UploadInput = { plan: PhotoUploadPlan; position: number };

// Blobs are immutable. Copy every mutable metadata object, retaining the exact bytes.
function cloneUpload(input: UploadInput): UploadInput {
  return { position: input.position, plan: { blurhash: input.plan.blurhash,
    original: { ...input.plan.original }, w480: { ...input.plan.w480 },
    w960: { ...input.plan.w960 }, w1600: { ...input.plan.w1600 } } };
}

export function PhotoManager({ businessId, photos: initialPhotos }: PhotoManagerProps) {
  const queryClient = useQueryClient();
  const gallery = useQuery({ queryKey: ['business-photos', businessId], queryFn: listBusinessPhotos });
  const photos = gallery.data?.data ?? initialPhotos;
  const [preparing, setPreparing] = useState(false);
  const [preparationError, setPreparationError] = useState<string>();
  const invalidate = () => { void queryClient.invalidateQueries({ queryKey: ['business-photos', businessId] }); void queryClient.invalidateQueries({ queryKey: ['business-detail', businessId] }); };
  const upload = useMutationAttempt({
    cloneInput: cloneUpload,
    mutationFn: (input: UploadInput, key: string) => uploadBusinessPhoto(input.plan, { position: input.position }, key),
    exito: 'Foto subida',
    onSuccess: invalidate,
  });
  async function handleUpload(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0]; event.target.value = '';
    if (!file || preparing || upload.isPending || upload.uncertain) return;
    setPreparing(true); setPreparationError(undefined); upload.reset();
    try { const plan = await buildPhotoUploadPlan(file); upload.mutate({ plan, position: photos.length }); }
    catch (error) { setPreparationError(errorMessage(error)); }
    finally { setPreparing(false); }
  }
  return <section className="section" aria-label="Fotos">
    <h2 className="section-label">Fotos</h2>
    {gallery.isError ? <><ErrorBanner message="No pudimos cargar tus fotos." /><button type="button" className="btn small" onClick={() => void gallery.refetch()}>Reintentar galería</button></> : null}
    <ul className="rows">{photos.map((photo, index) => <PhotoRow key={photo.id} photo={photo} index={index} total={photos.length} onChanged={invalidate} />)}</ul>
    <label htmlFor="photo-upload" className="btn small secondary photo-upload-label">{preparing || upload.isPending ? 'Subiendo…' : 'Agregar foto'}</label>
    <input id="photo-upload" type="file" accept="image/*" className="sr-only" onChange={handleUpload} disabled={preparing || upload.isPending || upload.uncertain} />
    {preparationError ? <ErrorBanner message={preparationError} /> : null}
    {upload.isError ? <ErrorBanner message={errorMessage(upload.error)} /> : null}
    {upload.uncertain ? <button type="button" className="btn small" onClick={upload.retry} disabled={upload.isPending}>Reintentar foto</button> : null}
  </section>;
}

type PhotoRowProps = { photo: BusinessPhoto; index: number; total: number; onChanged(): void };
type PhotoChange = { kind: 'move'; position: number } | { kind: 'primary' } | { kind: 'delete' };

function PhotoRow({ photo, index, total, onChanged }: PhotoRowProps) {
  const change = useMutationAttempt({
    mutationFn: (input: PhotoChange, key: string) => input.kind === 'delete'
      ? deleteBusinessPhoto(photo.id, key)
      : patchBusinessPhoto(photo.id, input.kind === 'primary' ? { isPrimary: true } : { position: input.position }, key),
    onSuccess: onChanged,
  });
  const disabled = change.isPending || change.uncertain;
  const thumbnail = photo.variants[0];
  return <li className="row photo-row">
    {thumbnail ? <img className="photo-thumb" src={thumbnail.url} alt="" width={80} height={80} /> : null}
    <div className="grow">
      {index === 0 ? <p className="status" data-tone="due">Principal</p> : null}
      {change.isError ? <ErrorBanner message={errorMessage(change.error)} /> : null}
      {change.uncertain ? <button type="button" className="btn small" disabled={change.isPending} onClick={change.retry}>Reintentar cambio</button> : null}
    </div>
    <span className="action-pair">
      <button type="button" className="btn small ghost" disabled={disabled || index === 0} onClick={() => change.mutate({ kind: 'move', position: index - 1 })}>Subir</button>
      <button type="button" className="btn small ghost" disabled={disabled || index === total - 1} onClick={() => change.mutate({ kind: 'move', position: index + 1 })}>Bajar</button>
      {index !== 0 ? <button type="button" className="btn small secondary" disabled={disabled} onClick={() => change.mutate({ kind: 'primary' })}>Hacer principal</button> : null}
      <button type="button" className="btn small danger" disabled={disabled} onClick={() => change.mutate({ kind: 'delete' })}>Eliminar</button>
    </span>
  </li>;
}

function errorMessage(error: unknown): string {
  return error instanceof ApiError ? error.message : 'No pudimos completar eso ahora mismo.';
}
